import type { RaAnswer, RaChecklistSource, RaVerificationStatus } from '@felix/domain';
import type { StatusTone } from '../../../StatusBadge.js';
import type {
  RaVerificationAnswerInput,
  RaVerificationItem,
  RaVerificationSummary,
} from './types.js';

/**
 * Operational Verification ekranının saf yardımcıları (K5; ADR 0089): süzgeç, sıra, durum tonu,
 * madde grupları ve cevap taslağı. Kural kararları domain'dedir (`raVerificationOutcome`,
 * `raVerificationNext`); burada yalnız görünüm.
 */

export const VERIFICATION_FILTERS = [
  'all',
  'open',
  'pending_authorization',
  'blocked',
  'active',
  'closed',
] as const;
export type VerificationFilter = (typeof VERIFICATION_FILTERS)[number];

/** Süzgecin durumları: açık aşama, engelli (On Hold, Escalated, Suspended, Reverification). */
const GROUPS: Record<Exclude<VerificationFilter, 'all'>, readonly RaVerificationStatus[]> = {
  open: ['draft', 'incomplete', 'verification_completed'],
  pending_authorization: ['pending_authorization'],
  blocked: ['on_hold', 'escalated', 'suspended', 'reverification_required'],
  active: ['authorized', 'in_progress'],
  closed: ['completed', 'closed', 'cancelled'],
};

export interface VerificationListFilter {
  query: string;
  state: VerificationFilter;
}
export const EMPTY_VERIFICATION_FILTER: VerificationListFilter = { query: '', state: 'all' };

const norm = (value: string | null | undefined) => (value ?? '').toLocaleLowerCase();

/** Durumun sırası: yetki bekleyen ve engelli başta, kapanmışlar sonda; sonra yeniden eskiye. */
const ORDER: Record<RaVerificationStatus, number> = {
  pending_authorization: 0,
  escalated: 1,
  on_hold: 1,
  reverification_required: 1,
  suspended: 1,
  in_progress: 2,
  authorized: 2,
  verification_completed: 3,
  incomplete: 3,
  draft: 3,
  completed: 4,
  closed: 5,
  cancelled: 5,
};

export function filterVerifications(
  items: readonly RaVerificationSummary[],
  filter: VerificationListFilter,
): RaVerificationSummary[] {
  const q = norm(filter.query.trim());
  return items
    .filter(
      (v) =>
        (filter.state === 'all' || GROUPS[filter.state].includes(v.status)) &&
        (q === '' ||
          norm(v.number).includes(q) ||
          norm(v.operation).includes(q) ||
          norm(v.location).includes(q) ||
          norm(v.code).includes(q) ||
          norm(v.title).includes(q)),
    )
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || b.createdAt.localeCompare(a.createdAt));
}

/** Özet sayıları (kartlar). */
export function verificationStats(items: readonly RaVerificationSummary[]) {
  const count = (group: Exclude<VerificationFilter, 'all'>) =>
    items.filter((v) => GROUPS[group].includes(v.status)).length;
  return {
    open: count('open'),
    pending: count('pending_authorization'),
    blocked: count('blocked'),
    active: count('active'),
  };
}

/** Durumun rozet tonu: "Authorized" yalnız sunucunun yazdığı yetkiden (BR-02). */
export function verificationTone(status: RaVerificationStatus): StatusTone {
  switch (status) {
    case 'authorized':
    case 'in_progress':
      return 'success';
    case 'pending_authorization':
    case 'verification_completed':
      return 'info';
    case 'on_hold':
    case 'escalated':
    case 'suspended':
    case 'reverification_required':
      return 'danger';
    case 'incomplete':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Maddeler kaynağa göre: ortak, faaliyete özel, zorunlu kontroller (checklist sırası korunur). */
export function groupItems(
  items: readonly RaVerificationItem[],
): { source: RaChecklistSource; items: RaVerificationItem[] }[] {
  const order: RaChecklistSource[] = ['common', 'activity', 'control'];
  return order
    .map((source) => ({ source, items: items.filter((i) => i.source === source) }))
    .filter((g) => g.items.length > 0);
}

/** Cevap taslağı: maddenin ekrandaki değeri. */
export interface AnswerDraft {
  answer: RaAnswer | null;
  comment: string;
  evidence: string;
}

export function draftOf(item: RaVerificationItem): AnswerDraft {
  return {
    answer: item.current?.answer ?? null,
    comment: item.current?.comment ?? '',
    evidence: item.current?.evidence ?? '',
  };
}

const clean = (value: string) => (value.trim() === '' ? null : value.trim());

/**
 * Kaydedilecek cevaplar: yalnız cevabı seçilmiş ve geçerli turdaki son cevaptan farklı olanlar
 * (sunucu da aynı cevabı ikinci kez yazmaz). No ve N/A'nın gerekçesi eksikse madde anahtarı
 * `invalid`'e düşer.
 */
export function changedAnswers(
  items: readonly RaVerificationItem[],
  drafts: Readonly<Record<string, AnswerDraft>>,
): { answers: RaVerificationAnswerInput[]; invalid: string[] } {
  const answers: RaVerificationAnswerInput[] = [];
  const invalid: string[] = [];
  for (const item of items) {
    const draft = drafts[item.key];
    if (!draft?.answer) continue;
    const next = {
      itemKey: item.key,
      answer: draft.answer,
      comment: clean(draft.comment),
      evidence: clean(draft.evidence),
    };
    if (next.answer !== 'yes' && next.comment === null) {
      invalid.push(item.key);
      continue;
    }
    const current = item.current;
    if (
      current &&
      current.answer === next.answer &&
      (current.comment ?? null) === next.comment &&
      (current.evidence ?? null) === next.evidence
    ) {
      continue;
    }
    answers.push(next);
  }
  return { answers, invalid };
}
